import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const Section = ({ title, children }) => (
  <div className="mb-10">
    <h2 className="text-lg font-semibold text-[#f5f5f5] mb-4">{title}</h2>
    <div className="text-[#a1a1aa] text-sm leading-relaxed space-y-3">
      {children}
    </div>
  </div>
);

const PolitiqueConfidentialite = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="politique-confidentialite-page">
      <Helmet>
        <title>{"Politique de confidentialit\u00e9 - Cloneia"}</title>
        <meta name="description" content="Politique de confidentialit\u00e9 et protection des donn\u00e9es personnelles du site cloneia.fr, conform\u00e9ment au RGPD." />
      </Helmet>

      <Header />

      <section className="pt-32 md:pt-40 pb-24 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/"
              className="inline-flex items-center gap-2 text-[#71717a] hover:text-[#3b82f6] text-sm mb-8 transition-colors"
              data-testid="politique-breadcrumb"
            >
              <ArrowLeft className="w-4 h-4" />
              {"Retour \u00e0 l\u2019accueil"}
            </Link>

            <h1 className="text-3xl sm:text-4xl font-bold text-[#f5f5f5] tracking-tight mb-4" data-testid="politique-title">
              {"Politique de confidentialit\u00e9"}
            </h1>
            <p className="text-[#71717a] text-sm mb-12">
              {"Derni\u00e8re mise \u00e0 jour : mars 2026"}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <Section title="1. Responsable du traitement">
              <p>{"Le responsable du traitement des donn\u00e9es \u00e0 caract\u00e8re personnel collect\u00e9es sur le site cloneia.fr est :"}</p>
              <div className="bg-[#0f0f17] border border-white/5 rounded-xl p-5 space-y-1.5">
                <p><span className="text-[#f5f5f5] font-medium">SA ENERGO SYM</span></p>
                <p>{"102, Avenue des Champs-\u00c9lys\u00e9es, 75008 Paris, France"}</p>
                <p>{"Courriel : "}<a href="mailto:hello@cloneia.fr" className="text-[#3b82f6] hover:underline">hello@cloneia.fr</a></p>
                <p>{"T\u00e9l\u00e9phone : 01 84 80 48 84"}</p>
              </div>
            </Section>

            <Section title={"2. Donn\u00e9es collect\u00e9es"}>
              <p>{"Dans le cadre de l\u2019utilisation du site cloneia.fr, nous sommes susceptibles de collecter les cat\u00e9gories de donn\u00e9es suivantes :"}</p>

              <div className="space-y-4 mt-2">
                <div className="bg-[#0f0f17] border border-white/5 rounded-xl p-5">
                  <p className="text-[#f5f5f5] font-medium mb-2">{"Formulaire de demande de d\u00e9monstration"}</p>
                  <p>{"Nom, pr\u00e9nom, adresse email professionnelle, num\u00e9ro de t\u00e9l\u00e9phone, nom de l\u2019entreprise."}</p>
                </div>

                <div className="bg-[#0f0f17] border border-white/5 rounded-xl p-5">
                  <p className="text-[#f5f5f5] font-medium mb-2">Formulaire de contact</p>
                  <p>{"Nom, adresse email, nom de l\u2019entreprise, objet de la demande, contenu du message."}</p>
                </div>

                <div className="bg-[#0f0f17] border border-white/5 rounded-xl p-5">
                  <p className="text-[#f5f5f5] font-medium mb-2">{"Donn\u00e9es de navigation (analytics)"}</p>
                  <p>{"Adresse IP (utilis\u00e9e pour la g\u00e9olocalisation puis anonymis\u00e9e), type d\u2019appareil (ordinateur, mobile, tablette), pages visit\u00e9es, dur\u00e9e de visite, source de trafic (referrer), profondeur de d\u00e9filement. Un identifiant anonyme est g\u00e9n\u00e9r\u00e9 et stock\u00e9 dans le navigateur (localStorage) pour distinguer les visiteurs uniques."}</p>
                </div>
              </div>
            </Section>

            <Section title={"3. Finalit\u00e9s et bases l\u00e9gales du traitement"}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm border-collapse">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left text-[#f5f5f5] font-medium py-3 pr-4">{"Finalit\u00e9"}</th>
                      <th className="text-left text-[#f5f5f5] font-medium py-3">{"Base l\u00e9gale"}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    <tr>
                      <td className="py-3 pr-4 text-[#a1a1aa]">{"Traitement des demandes de d\u00e9monstration"}</td>
                      <td className="py-3 text-[#a1a1aa]">{"Ex\u00e9cution de mesures pr\u00e9contractuelles (art. 6.1.b RGPD)"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 text-[#a1a1aa]">{"R\u00e9ponse aux demandes de contact"}</td>
                      <td className="py-3 text-[#a1a1aa]">{"Int\u00e9r\u00eat l\u00e9gitime (art. 6.1.f RGPD)"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 text-[#a1a1aa]">{"Mesure d\u2019audience et am\u00e9lioration du site"}</td>
                      <td className="py-3 text-[#a1a1aa]">{"Int\u00e9r\u00eat l\u00e9gitime (art. 6.1.f RGPD) \u2014 traceur exempt de consentement (d\u00e9lib\u00e9ration CNIL n\u00b02020-091)"}</td>
                    </tr>
                    <tr>
                      <td className="py-3 pr-4 text-[#a1a1aa]">{"Envoi de communications commerciales"}</td>
                      <td className="py-3 text-[#a1a1aa]">{"Consentement (art. 6.1.a RGPD)"}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Section>

            <Section title={"4. Dur\u00e9e de conservation"}>
              <div className="space-y-2">
                <div className="flex items-start gap-3 bg-[#0f0f17] border border-white/5 rounded-xl p-4">
                  <div className="w-2 h-2 rounded-full bg-[#3b82f6] mt-1.5 shrink-0" />
                  <div>
                    <p className="text-[#f5f5f5] font-medium">{"Donn\u00e9es des formulaires (d\u00e9mo et contact)"}</p>
                    <p className="text-[#71717a] text-xs mt-0.5">{"3 ans \u00e0 compter du dernier contact"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 bg-[#0f0f17] border border-white/5 rounded-xl p-4">
                  <div className="w-2 h-2 rounded-full bg-[#8b5cf6] mt-1.5 shrink-0" />
                  <div>
                    <p className="text-[#f5f5f5] font-medium">{"Donn\u00e9es de navigation et analytics"}</p>
                    <p className="text-[#71717a] text-xs mt-0.5">{"13 mois conform\u00e9ment aux recommandations de la CNIL"}</p>
                  </div>
                </div>
              </div>
              <p className="mt-3">{"À l\u2019expiration de ces d\u00e9lais, les donn\u00e9es sont supprim\u00e9es ou anonymis\u00e9es de mani\u00e8re irr\u00e9versible."}</p>
            </Section>

            <Section title={"5. Destinataires des donn\u00e9es"}>
              <p>{"Les donn\u00e9es collect\u00e9es sont destin\u00e9es uniquement aux \u00e9quipes internes de SA ENERGO SYM (\u00e9quipes commerciale, technique et direction). Elles ne sont en aucun cas c\u00e9d\u00e9es, vendues ou lou\u00e9es \u00e0 des tiers."}</p>
              <p>{"Les sous-traitants techniques suivants peuvent avoir acc\u00e8s aux donn\u00e9es dans le cadre strict de leurs prestations :"}</p>
              <ul className="list-disc list-inside space-y-1 text-[#a1a1aa] ml-2">
                <li>{"OVH SAS (h\u00e9bergement du site et de la base de donn\u00e9es, localis\u00e9 en France)"}</li>
                <li>{"OVH SAS (service de messagerie SMTP pour l\u2019envoi de notifications)"}</li>
              </ul>
            </Section>

            <Section title={"6. Transfert de donn\u00e9es hors Union europ\u00e9enne"}>
              <p>{"L\u2019ensemble des donn\u00e9es collect\u00e9es sur le site cloneia.fr sont h\u00e9berg\u00e9es en France par OVH SAS. "}<strong className="text-[#f5f5f5]">{"Aucun transfert de donn\u00e9es hors de l\u2019Union europ\u00e9enne n\u2019est effectu\u00e9."}</strong></p>
            </Section>

            <Section title="7. Cookies et traceurs">
              <p>{"Le site cloneia.fr utilise un "}<strong className="text-[#f5f5f5]">{"syst\u00e8me d\u2019analyse de trafic propri\u00e9taire"}</strong>{" qui ne d\u00e9pose aucun cookie au sens r\u00e9glementaire du terme. Ce syst\u00e8me est conforme aux conditions d\u2019exemption de consentement d\u00e9finies par la CNIL (d\u00e9lib\u00e9ration n\u00b02020-091 du 17 septembre 2020)."}</p>
              <p>Ce traceur :</p>
              <ul className="list-disc list-inside space-y-1 text-[#a1a1aa] ml-2">
                <li>{"Ne permet pas le suivi de la navigation entre diff\u00e9rents sites"}</li>
                <li>{"Ne croise pas les donn\u00e9es avec d\u2019autres traitements"}</li>
                <li>{"Ne transmet aucune donn\u00e9e \u00e0 des tiers"}</li>
                <li>{"Sert exclusivement \u00e0 produire des statistiques anonymes d\u2019audience"}</li>
              </ul>
              <p>{"Aucun cookie tiers (Google Analytics, Facebook Pixel, etc.) n\u2019est utilis\u00e9 sur ce site."}</p>
            </Section>

            <Section title="8. Vos droits">
              <p>{"Conform\u00e9ment au R\u00e8glement G\u00e9n\u00e9ral sur la Protection des Donn\u00e9es (RGPD) et \u00e0 la loi Informatique et Libert\u00e9s, vous disposez des droits suivants concernant vos donn\u00e9es personnelles :"}</p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
                {[
                  { title: "Droit d\u2019acc\u00e8s", desc: "Obtenir la confirmation que des donn\u00e9es vous concernant sont trait\u00e9es et en recevoir une copie." },
                  { title: "Droit de rectification", desc: "Demander la correction de donn\u00e9es inexactes ou incompl\u00e8tes." },
                  { title: "Droit \u00e0 l\u2019effacement", desc: "Demander la suppression de vos donn\u00e9es dans les conditions pr\u00e9vues par le RGPD." },
                  { title: "Droit d\u2019opposition", desc: "Vous opposer au traitement de vos donn\u00e9es pour des motifs l\u00e9gitimes." },
                  { title: "Droit \u00e0 la portabilit\u00e9", desc: "Recevoir vos donn\u00e9es dans un format structur\u00e9 et lisible par machine." },
                  { title: "Droit \u00e0 la limitation", desc: "Demander la suspension du traitement de vos donn\u00e9es dans certains cas." },
                ].map((right) => (
                  <div key={right.title} className="bg-[#0f0f17] border border-white/5 rounded-xl p-4">
                    <p className="text-[#f5f5f5] font-medium text-sm mb-1">{right.title}</p>
                    <p className="text-[#71717a] text-xs">{right.desc}</p>
                  </div>
                ))}
              </div>

              <p className="mt-4">{"Pour exercer vos droits, vous pouvez nous contacter :"}</p>
              <ul className="list-disc list-inside space-y-1 text-[#a1a1aa] ml-2">
                <li>{"Par email : "}<a href="mailto:hello@cloneia.fr" className="text-[#3b82f6] hover:underline">hello@cloneia.fr</a></li>
                <li>{"Par courrier : SA ENERGO SYM, 102 Avenue des Champs-\u00c9lys\u00e9es, 75008 Paris"}</li>
              </ul>
              <p>{"Nous nous engageons \u00e0 r\u00e9pondre \u00e0 votre demande dans un d\u00e9lai d\u2019un mois \u00e0 compter de sa r\u00e9ception."}</p>
            </Section>

            <Section title={"9. R\u00e9clamation aupr\u00e8s de la CNIL"}>
              <p>{"Si vous estimez que le traitement de vos donn\u00e9es personnelles constitue une violation du RGPD, vous avez le droit d\u2019introduire une r\u00e9clamation aupr\u00e8s de la Commission Nationale de l\u2019Informatique et des Libert\u00e9s (CNIL) :"}</p>
              <div className="bg-[#0f0f17] border border-white/5 rounded-xl p-5 space-y-1.5">
                <p><span className="text-[#f5f5f5] font-medium">CNIL</span></p>
                <p>3 Place de Fontenoy, TSA 80715, 75334 Paris Cedex 07</p>
                <p>{"Site web : "}<a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-[#3b82f6] hover:underline">www.cnil.fr</a></p>
              </div>
            </Section>

            <Section title="10. Modifications de la politique">
              <p>{"La pr\u00e9sente politique de confidentialit\u00e9 peut \u00eatre modifi\u00e9e \u00e0 tout moment afin de s\u2019adapter aux \u00e9volutions l\u00e9gislatives ou aux modifications apport\u00e9es au site. La date de derni\u00e8re mise \u00e0 jour est indiqu\u00e9e en haut de cette page. Nous vous invitons \u00e0 la consulter r\u00e9guli\u00e8rement."}</p>
            </Section>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PolitiqueConfidentialite;
